/**
 * Generic Alias CRUD Hooks
 *
 * These hooks provide a unified API for managing aliases across all entity types.
 * Supported entity types: 'division', 'product_group', 'company', 'supplier'
 */

import { useMutation } from '@tanstack/react-query';
import useGetApi from 'src/api/useGetApi';
import config from 'src/config';
import ApiError from 'src/api/ApiError';
import type ApiResponse from 'src/api/ApiResponse';
import { useToast } from 'src/context/ToastContext';

// Supported entity types
export type AliasEntityType =
  | 'division'
  | 'product_group'
  | 'company'
  | 'supplier';

export interface AliasItem {
  id: string;
  alias: string;
  addedByUserId: string | null;
  addedByUserName: string | null;
  createdAt: string;
}

interface ListAliasesResponse {
  aliases: AliasItem[];
}

/**
 * Fetch all aliases for an entity
 */
export function useAliases(
  entityType: AliasEntityType,
  entityId: string,
  enabled = true,
) {
  return useGetApi<ListAliasesResponse>({
    endpoint: `core/aliases/${entityType}/${entityId}`,
    options: {
      enabled: enabled && !!entityId,
    },
  });
}

interface UpsertAliasesRequest {
  entityType: AliasEntityType;
  entityId: string;
  aliases: string[];
}

interface UpsertAliasesResponse {
  created: number;
  skipped: number;
  total: number;
}

/**
 * Upsert aliases for an entity (batch create)
 */
export function useUpsertAliases() {
  const { showToast } = useToast();
  return useMutation<
    ApiResponse<UpsertAliasesResponse>,
    ApiError,
    UpsertAliasesRequest
  >({
    mutationFn: async ({ entityType, entityId, aliases }) => {
      const res = await fetch(
        `${config.apiBaseUrl}/core/aliases/${entityType}/${entityId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ aliases }),
        },
      );
      if (!res.ok) {
        let message = 'Unknown error';
        try {
          const body = await res.json();
          message = body?.error ?? body?.message ?? 'Unknown error';
        } catch {
          message = `Request failed with status ${res.status}`;
        }
        throw new ApiError(res.status, message);
      }
      return (await res.json()) as ApiResponse<UpsertAliasesResponse>;
    },
    onSuccess: () => {
      showToast({
        title: 'Success',
        message: 'Alias saved',
        color: 'success',
        icon: 'check',
      });
    },
    onError: (e) => {
      showToast({
        title: 'Error',
        message: e.message || 'Error saving alias',
        color: 'error',
        icon: 'error',
      });
    },
  });
}

interface DeleteAliasRequest {
  entityType: AliasEntityType;
  entityId: string;
  alias: string;
}

interface DeleteAliasResponse {
  deleted: number;
}

/**
 * Delete an alias from an entity
 */
export function useDeleteAlias() {
  const { showToast } = useToast();
  return useMutation<
    ApiResponse<DeleteAliasResponse>,
    ApiError,
    DeleteAliasRequest
  >({
    mutationFn: async ({ entityType, entityId, alias }) => {
      const res = await fetch(
        `${config.apiBaseUrl}/core/aliases/${entityType}/${entityId}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ alias }),
        },
      );
      if (!res.ok) {
        let message = 'Unknown error';
        try {
          const body = await res.json();
          message = body?.error ?? body?.message ?? 'Unknown error';
        } catch {
          message = `Request failed with status ${res.status}`;
        }
        throw new ApiError(res.status, message);
      }
      return (await res.json()) as ApiResponse<DeleteAliasResponse>;
    },
    onSuccess: () => {
      showToast({
        title: 'Success',
        message: 'Alias removed',
        color: 'success',
        icon: 'check',
      });
    },
    onError: (e) => {
      showToast({
        title: 'Error',
        message: e.message || 'Error removing alias',
        color: 'error',
        icon: 'error',
      });
    },
  });
}
